import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWallet } from "../lib/wallet";
import { truncate } from "../lib/web3";
import { Wallet } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_polar-token-land/artifacts/h5uomzcw_immagine.png";

export default function Header() {
    const { t } = useTranslation();
    const { account, connect, busy } = useWallet();
    const loc = useLocation();

    const links = [
        { to: "/", label: t("nav.home") },
        { to: "/explorer", label: t("nav.explorer") },
        { to: "/auctions", label: t("nav.auctions") },
        { to: "/my-tokens", label: t("nav.my_tokens") },
        { to: "/about", label: t("nav.about") },
    ];

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/70 backdrop-blur-xl"
        >
            <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center gap-3 group shrink-0"
                >
                    <img
                        src={LOGO_URL}
                        alt="The Portal"
                        className="h-10 w-10 object-contain rounded-full ring-1 ring-white/10 group-hover:ring-frost/60 transition-all"
                    />
                    <span className="font-serif text-lg tracking-[0.3em] uppercase leading-none hidden sm:inline">
                        The <span className="text-frost">Portal</span>
                        <span className="block text-[9px] tracking-[0.4em] text-slate-500 mt-1">
                            {t("nav.tagline")}
                        </span>
                    </span>
                </Link>
                <nav className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                `px-3 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${
                                    isActive || loc.pathname === l.to
                                        ? "text-white"
                                        : "text-slate-400 hover:text-white"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-2">
                    <LanguageSwitcher />
                    <button
                        data-testid="connect-wallet-button"
                        onClick={connect}
                        disabled={busy}
                        className="btn-sharp inline-flex items-center gap-2 border border-white/20 hover:border-frost hover:text-frost px-4 py-2 text-xs disabled:opacity-50"
                    >
                        <Wallet className="w-3.5 h-3.5" />
                        {account
                            ? truncate(account)
                            : busy
                              ? t("nav.connecting")
                              : t("nav.connect")}
                    </button>
                </div>
            </div>
        </header>
    );
}
